import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  DeleteForever as RemoveIcon,
  Map as MapIcon,
  MyLocation as MyLocationIcon,
  PowerOff as UnplugIcon,
  RestoreFromTrash as RestoreIcon,
  RvHookup as TrailerIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import {
  AnyTrackLocation,
  useRemoveDeviceMutation,
  useRestoreDeviceMutation,
} from "../../../../api/anyTrackApi";
import { useAppSelector } from "../../../../app/hooks";
import { UserRoles } from "../../../../constants/roles";
import { selectCurrentUser } from "../../../auth/authSlice";
import { ShareLinkDialog } from "./ShareLinkDialog";

interface TrailerTooltipProps {
  selectedTrailer: AnyTrackLocation | undefined;
  isSidebarOpen: boolean;
  trailerSidebarOpen?: boolean;
  onClose?: () => void;
  onCenterTrailer?: () => void;
}

const DRIVING_STATUS_CONFIG = {
  0: { label: "Idle", color: "#9E9E9E" },
  1: { label: "Driving", color: "#4CAF50" },
  2: { label: "ACC Off", color: "#455A64" },
};

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
}

export const TrailerTooltip: React.FC<TrailerTooltipProps> = ({
  selectedTrailer,
  isSidebarOpen,
  trailerSidebarOpen = true,
  onClose,
  onCenterTrailer,
}) => {
  const theme = useTheme();
  const user = useAppSelector(selectCurrentUser);
  const canShareLiveLocation = user?.role !== UserRoles.TRUCK_DRIVER;
  const canManageDevices = user?.role !== UserRoles.TRUCK_DRIVER;
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [removeDevice] = useRemoveDeviceMutation();
  const [restoreDevice] = useRestoreDeviceMutation();

  if (!selectedTrailer) {
    return null;
  }

  const isUnplugged = selectedTrailer.deviceStatus === "Unplugged";
  const isRemoved = selectedTrailer.isRemoved;
  const hasCoordinates =
    selectedTrailer.latitude != null && selectedTrailer.longitude != null;
  const displayName =
    selectedTrailer.vehicleName?.trim() || selectedTrailer.deviceId.slice(-8);
  const statusKey = selectedTrailer.drivingStatus as 0 | 1 | 2 | null;
  const statusConfig =
    statusKey !== null && statusKey in DRIVING_STATUS_CONFIG
      ? DRIVING_STATUS_CONFIG[statusKey as keyof typeof DRIVING_STATUS_CONFIG]
      : null;
  const vehicleSidebarWidth = 280;
  const trailerSidebarWidth = 260;
  const tooltipLeft =
    (isSidebarOpen ? vehicleSidebarWidth : 0) +
    (trailerSidebarOpen ? trailerSidebarWidth : 0) +
    16;

  const mapActionIconSx = {
    border: 1,
    borderColor: "warning.main",
    color: "warning.dark",
    borderRadius: 1,
    width: 30,
    height: 30,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "warning.main",
      color: "warning.contrastText",
      borderColor: "warning.dark",
    },
  };

  return (
    <>
      <Card
        sx={{
          position: "absolute",
          top: 16,
          left: tooltipLeft,
          width: 360,
          maxWidth: `calc(100vw - ${tooltipLeft + 24}px)`,
          zIndex: 999,
          boxShadow: 3,
          borderRadius: 2,
          bgcolor:
            theme.palette.mode === "light"
              ? "rgba(255, 255, 255, 0.95)"
              : "rgba(33, 33, 33, 0.95)",
          backdropFilter: "blur(8px)",
          border: "1px solid",
          borderColor: theme.palette.divider,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close trailer tooltip"
            sx={{
              position: "absolute",
              top: -14,
              right: -14,
              zIndex: 2,
              bgcolor:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(33, 33, 33, 0.9)",
              boxShadow: 1,
              "&:hover": { bgcolor: "error.main", color: "white" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isUnplugged ? (
                <UnplugIcon sx={{ color: "#f44336", flexShrink: 0 }} />
              ) : (
                <TrailerIcon sx={{ color: "#ca8a04", flexShrink: 0 }} />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700 }}
                  noWrap
                  title={displayName}
                >
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {selectedTrailer.licensePlateNumber || selectedTrailer.deviceId}
                </Typography>
              </Box>
              {statusConfig && (
                <Chip
                  size="small"
                  label={statusConfig.label}
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: `${statusConfig.color}25`,
                    border: `1px solid ${statusConfig.color}50`,
                    color: statusConfig.color,
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
                {selectedTrailer.speed != null && (
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(selectedTrailer.speed * 0.621371)} mph
                  </Typography>
                )}
                {selectedTrailer.batteryLevel != null && (
                  <Typography variant="caption" color="text.secondary">
                    Battery {Math.round(selectedTrailer.batteryLevel)}%
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={0.75}>
                {onCenterTrailer && hasCoordinates && (
                  <Tooltip title="Center on trailer">
                    <IconButton
                      size="small"
                      onClick={onCenterTrailer}
                      aria-label="Center on trailer"
                      sx={mapActionIconSx}
                    >
                      <MyLocationIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {hasCoordinates && (
                  <Tooltip title="Open location in Google Maps">
                    <IconButton
                      size="small"
                      aria-label="Open trailer location in Google Maps"
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${selectedTrailer.latitude},${selectedTrailer.longitude}`;
                        window.open(url, "_blank");
                      }}
                      sx={mapActionIconSx}
                    >
                      <MapIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {hasCoordinates && (
                  <Tooltip title="Copy coordinates to clipboard">
                    <IconButton
                      size="small"
                      aria-label="Copy trailer coordinates to clipboard"
                      onClick={async () => {
                        const coordinates = `${selectedTrailer.latitude},${selectedTrailer.longitude}`;
                        await copyText(coordinates);
                      }}
                      sx={mapActionIconSx}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {canShareLiveLocation && (
                  <Tooltip title="Share live location">
                    <IconButton
                      size="small"
                      aria-label="Share trailer live location"
                      onClick={() => setShareDialogOpen(true)}
                      sx={mapActionIconSx}
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            {/* Device management actions */}
            {canManageDevices && (isUnplugged || isRemoved) && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {isUnplugged && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<RemoveIcon sx={{ fontSize: "0.85rem !important" }} />}
                    onClick={() => removeDevice({ deviceId: selectedTrailer.deviceId })}
                    sx={{ fontSize: "0.65rem", py: 0.25, px: 1, textTransform: "none" }}
                  >
                    Remove Device
                  </Button>
                )}
                {isRemoved && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<RestoreIcon sx={{ fontSize: "0.85rem !important" }} />}
                    onClick={() => restoreDevice(selectedTrailer.deviceId)}
                    sx={{ fontSize: "0.65rem", py: 0.25, px: 1, textTransform: "none" }}
                  >
                    Restore Device
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <ShareLinkDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        initialTrailer={selectedTrailer}
      />
    </>
  );
};
